import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface FilterOption {
  value: string;
  label: string;
}

/**
 * The "VENDOR All" dropdown chip. Label is mono and dim, the selected value is mono and bright -
 * the contrast is what makes a set filter legible at a glance.
 */
export function FilterChip({
  label,
  value,
  options,
  onChange,
  disabled,
  className
}: {
  label: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  /** Renders the chip inert. Preferable to an `onChange` that silently does nothing. */
  disabled?: boolean;
  className?: string;
}) {
  const selected = options.find(option => option.value === value);
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        disabled={disabled}
        aria-disabled={disabled}
        className={cn(
          'inline-flex items-center gap-2 rounded border border-border bg-card px-3 py-2',
          'transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-border-strong',
          className
        )}
      >
        <span className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase">{label}</span>
        <span className="font-mono text-[10px] tracking-[0.12em] text-foreground uppercase">
          {selected?.label ?? 'All'}
        </span>
        <ChevronDown className="size-3 text-dim" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="start"
          className="z-50 min-w-40 rounded border border-border bg-card p-1 shadow-lg"
        >
          {options.map(option => (
            <DropdownMenu.Item
              key={option.value}
              onSelect={() => onChange(option.value)}
              className={cn(
                'cursor-pointer rounded-[3px] px-2 py-1.5 text-[13px] outline-none',
                option.value === value ? 'text-foreground' : 'text-muted',
                'data-highlighted:bg-raised data-highlighted:text-foreground'
              )}
            >
              {option.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
